import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  InsuranceType,
  LifeInsurancePermanentTypeEnum,
  LifeInsurancePolicyTypeEnum,
} from '../../api/useInsuranceQuery';
import { Box } from '../../../../CommonComponents/OPL/Box/Box';
import { InstitutionNameInput } from '../../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/components/InstitutionNameInput/InstitutionNameInput';
import { AccountOwnerSelect } from '../components/AccountOwnerSelect/AccountOwnerSelect';
import { AccountDescription } from '../components/AccountDescription/AccountDescription';
import { IsBrokerOfRecord } from '../components/IsBrokerOfRecord/IsBrokerOfRecord';
import { CoverageType } from '../components/CoverageType/CoverageType';
import { Premium } from '../components/Premium/Premium';
import { LifeInsurancePolicyType } from '../components/LifeInsurancePolicyType/LifeInsurancePolicyType';
import { LifeInsurancePolicyAmount } from '../components/LifeInsurancePolicyAmount/LifeInsurancePolicyAmount';
import { CashBalance } from '../components/CashBalance/CashBalance';
import {
  FormFields,
  FormProps,
} from '../../components/UpsertInsuranceModal/UpsertInsuranceModal.meta';
import { PremiumEndYear } from '../components/PremiumEndYear/PremiumEndYear';
import { LifeInsurancePermanentType } from '../components/LifeInsurancePermanentType/LifeInsurancePermanentType';
import { LifeInsuranceGrowthRate } from '../components/LifeInsuranceGrowthRate/LifeInsuranceGrowthRate';
import { LifeInsurancePolicyTerm } from '../components/LifeInsurancePolicyTerm/LifeInsurancePolicyTerm';
import { LifeInsurancePolicyStartYear } from '../components/LifeInsurancePolicyStartYear/LifeInsurancePolicyStartYear';
import { InsuredSelect } from '../components/InsuredSelect/InsuredSelect';
import { TabsContent } from '../components/TabsContent/TabsContent';
import { InsuranceData } from '../../useInsurance';
import { GrossEstateCheckbox } from '../components/GrossEstateCheckbox/GrossEstateCheckbox';
import { ManagementStyles } from '../components/ManagementStyles/ManagementStyles';
import { AssetBucketing } from '../components/AssetBucketing/AssetBucketing';

interface TabProps {
  isEstatePlanningWorkflow: boolean;
  data: InsuranceData;
  setAlert: (message: string) => void;
}

export const Summary = ({ isEstatePlanningWorkflow, data, setAlert }: TabProps) => {
  const formBag = useFormContext<FormFields>();

  const lifeInsuranceType = formBag.watch('LifeInsurancePolicyType');

  return (
    <Box display="flex" flexDirection="column" mt="16" css="gap: 16px">
      <InstitutionNameInput isInsurance />

      <AccountOwnerSelect
        label="Policy Owner"
        data={data}
        insuranceType={InsuranceType.Life}
        setAlert={setAlert}
      />

      <InsuredSelect data={data} />

      <AccountDescription />

      <LifeInsurancePolicyAmount />

      <LifeInsurancePolicyType data={data} />

      <ManagementStyles data={data} />

      <AssetBucketing data={data} />

      {lifeInsuranceType === LifeInsurancePolicyTypeEnum.Permanent && <CashBalance />}

      {isEstatePlanningWorkflow && <GrossEstateCheckbox data={data} />}

      <IsBrokerOfRecord />
    </Box>
  );
};

export const PolicyDetails = ({ data }: TabProps) => {
  const formBag = useFormContext<FormFields>();

  const [lifeInsuranceType, lifeInsurancePermanentType] = formBag.watch([
    'LifeInsurancePolicyType',
    'LifeInsurancePermanentType',
  ]);

  const isPermanent = lifeInsuranceType === LifeInsurancePolicyTypeEnum.Permanent;
  const isTerm = lifeInsuranceType === LifeInsurancePolicyTypeEnum.Term;
  const isVariableLife = lifeInsurancePermanentType === LifeInsurancePermanentTypeEnum.VariableLife;

  return (
    <Box display="flex" flexDirection="column" mt="16" css="gap: 16px">
      <CoverageType data={data} />

      {isPermanent && <LifeInsurancePermanentType data={data} />}

      {isTerm && (
        <>
          <LifeInsurancePolicyTerm data={data} />

          <LifeInsurancePolicyStartYear data={data} />
        </>
      )}

      <Premium />

      {isPermanent && <PremiumEndYear data={data} />}

      {isPermanent && !isVariableLife && <LifeInsuranceGrowthRate />}
    </Box>
  );
};

const tabs = [
  {
    name: 'Summary',
    Component: Summary,
  },
  {
    name: 'Policy Details',
    Component: PolicyDetails,
  },
];

export const LifeInsuranceForm = (props: FormProps) => {
  return <TabsContent tabs={tabs} {...props} />;
};
